<!-- Hub — minimal balances + links to paper / live hosts (dashboard.* only). -->
<script>
  import { onMount, onDestroy } from 'svelte'
  import { checkAuth, logout, replace } from '../api.js'
  import { authUser, executionInfo, paperData } from '../stores.js'
  import { startPolling, stopPolling } from '../lib/polling.js'
  import { fmtNum } from '../lib/format.js'
  import { link } from '../lib/historyNav.js'
  import { SITE_ORIGINS, defaultPathForSite } from '../lib/hostSite.js'
  import ThemeToggle from '../components/ThemeToggle.svelte'
  import OfflineBanner from '../components/OfflineBanner.svelte'
  import CoinTradingBar from '../components/CoinTradingBar.svelte'

  $: ex = $executionInfo || {}
  $: ready = !!$paperData
  $: paperPairs = Array.isArray($paperData && $paperData.paper_pairs)
    ? $paperData.paper_pairs
    : []

  function money(v) {
    if (v == null || !Number.isFinite(Number(v))) return '—'
    return fmtNum(Number(v))
  }

  function dayTone(p) {
    if (!p) return null
    const raw = p.day_delta
    if (raw == null || !Number.isFinite(Number(raw))) return null
    const d = Number(raw)
    if (Math.abs(d) < 0.005) return null
    return d > 0 ? 'up' : 'down'
  }

  $: livePusd = (() => {
    if (ex.onchain_pusd_balance != null && Number.isFinite(Number(ex.onchain_pusd_balance))) {
      return money(ex.onchain_pusd_balance)
    }
    const assumed = ex.smoke_assumed_bankroll
    if (assumed != null && Number.isFinite(Number(assumed))) return money(assumed)
    return '—'
  })()

  $: liveCashHint = (() => {
    if (ex.kill_switch_active || ex.live_15m_unit_active === false) return 'пауза · не торгует'
    if (ex.onchain_pusd_ok && ex.onchain_pusd_balance != null) return 'pUSD on-chain'
    if (ex.smoke_assumed_bankroll != null) return 'из конфига'
    return 'live cash'
  })()

  $: livePaused = !!(ex.kill_switch_active || ex.live_15m_unit_active === false)

  $: metaBits = (() => {
    const d = $paperData
    const user = (d && d.user) || $authUser || ''
    if (!d) return user || 'Загрузка…'
    return user || ''
  })()

  onMount(async () => {
    const a = await checkAuth()
    if (!a.ok) {
      replace('/login?next=' + encodeURIComponent(defaultPathForSite('hub')))
      return
    }
    authUser.set(a.user)
    startPolling()
  })

  onDestroy(() => {
    stopPolling()
  })
</script>

<OfflineBanner />

<svelte:head>
  <title>Lunes Crypto — хаб</title>
</svelte:head>

<div class="pm-atmosphere" aria-hidden="true"><div class="pm-atmosphere__grain"></div></div>

<div class="hub">
  <header class="hub__head">
    <div>
      <p class="pm-eyebrow">lunes</p>
      <h1 class="hub__title">Дашборд</h1>
      {#if metaBits}
        <p class="pm-meta">{metaBits}</p>
      {/if}
    </div>
    <div class="hub__tools">
      <a class="hub__settings" href="/hub/settings" use:link title="Настройки">Настройки</a>
      <ThemeToggle variant="nav" />
      <button type="button" class="pm-btn pm-btn--ghost" on:click={() => logout()}>Выйти</button>
    </div>
  </header>

  <section class="hub__balances" aria-label="Балансы">
    <div class="hub__bal hub__bal--live" class:hub__bal--paused={livePaused}>
      <p class="hub__bal-label">Live{#if livePaused}<span class="hub__pause"> · пауза</span>{/if}</p>
      <p class="hub__bal-value">{livePusd}<span class="hub__bal-unit">pUSD</span></p>
      <p class="hub__bal-sub">{liveCashHint}</p>
    </div>
  </section>

  <CoinTradingBar mode="paper" compact on:changed={() => {}} />

  <section class="hub__pairs" aria-label="Paper кошельки">
    <p class="hub__pairs-title">Paper · монета × TF</p>
    {#if !ready}
      <p class="pm-empty">Загрузка…</p>
    {:else if !paperPairs.length}
      <p class="pm-empty">Нет paper_pairs</p>
    {:else}
      <div class="hub__pairs-grid">
        {#each paperPairs as p (`${p.asset}:${p.horizon}:${p.brain || 'classic'}`)}
          <div
            class="hub__pair"
            class:is-up={dayTone(p) === 'up'}
            class:is-down={dayTone(p) === 'down'}
          >
            <span class="hub__pair-lab">{p.label || `${String(p.asset).toUpperCase()} ${p.horizon}`}</span>
            <strong class="hub__pair-bal mono"
              >{p.balance != null && Number.isFinite(Number(p.balance))
                ? Number(p.balance).toFixed(0)
                : '—'}</strong
            >
            <span class="hub__pair-wr mono"
              >{p.win_rate != null && Number.isFinite(Number(p.win_rate))
                ? `${Math.round(Number(p.win_rate) * 100)}%`
                : '—'}</span
            >
          </div>
        {/each}
      </div>
    {/if}
  </section>

  <nav class="hub__nav" aria-label="Разделы">
    <a class="hub__go hub__go--paper" href={SITE_ORIGINS.paper}>
      <span class="hub__go-name">Paper</span>
      <span class="hub__go-desc">Обзор · мозг · ставки</span>
    </a>
    <a class="hub__go hub__go--live" href={SITE_ORIGINS.live}>
      <span class="hub__go-name">Live</span>
      <span class="hub__go-desc">Живая торговля · pUSD</span>
    </a>
    <a class="hub__go hub__go--ai" href={SITE_ORIGINS.ai}>
      <span class="hub__go-name">AI</span>
      <span class="hub__go-desc">Paper · ETH · sidebar</span>
    </a>
    <a class="hub__go hub__go--client" href={`${SITE_ORIGINS.paper}/dashboard/client`}>
      <span class="hub__go-name">Клиент</span>
      <span class="hub__go-desc">Andrey · Mini App · отзыв</span>
    </a>
  </nav>
</div>

<style>
  .hub {
    position: relative;
    z-index: 1;
    max-width: 42rem;
    margin: 0 auto;
    padding: clamp(1.5rem, 4vw, 2.75rem) clamp(1.1rem, 3vw, 1.75rem) 3rem;
    min-height: 100dvh;
    display: flex;
    flex-direction: column;
    gap: 2rem;
    box-sizing: border-box;
  }
  .hub__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
  }
  .hub__title {
    margin: 0.15rem 0 0;
    font-size: clamp(1.75rem, 4vw, 2.35rem);
    font-weight: 700;
    letter-spacing: -0.04em;
    color: var(--pm-ink);
  }
  .hub__tools {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    flex-shrink: 0;
  }
  .hub__settings {
    margin-right: 0.15rem;
    padding: 0.35rem 0.65rem;
    border-radius: var(--pm-radius);
    font-size: 0.82rem;
    font-weight: 600;
    color: var(--pm-ink-mute);
    text-decoration: none;
    border: 1px solid transparent;
  }
  .hub__settings:hover {
    color: var(--pm-ink);
    border-color: var(--pm-line);
    background: var(--pm-elevated-bg);
  }
  .hub__balances {
    display: grid;
    gap: 1rem 1.35rem;
    grid-template-columns: minmax(0, 1fr);
  }
  .hub__bal-label {
    margin: 0 0 0.35rem;
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--pm-ink-mute);
  }
  .hub__bal-value {
    margin: 0;
    font-size: clamp(1.65rem, 3.5vw, 2.15rem);
    font-weight: 700;
    letter-spacing: -0.045em;
    line-height: 1;
    color: var(--pm-ink);
    font-variant-numeric: tabular-nums;
  }
  .hub__bal-unit {
    margin-left: 0.28rem;
    font-size: 0.38em;
    font-weight: 600;
    letter-spacing: 0.08em;
    color: var(--pm-ink-mute);
    vertical-align: 0.35em;
  }
  .hub__bal-sub {
    margin: 0.4rem 0 0;
    font-size: 0.78rem;
    color: var(--pm-ink-mute);
  }
  .hub__bal--live .hub__bal-value {
    color: var(--pm-live);
  }
  .hub__bal--paused .hub__bal-value {
    color: var(--pm-ink-mute);
  }
  .hub__pause {
    font-weight: 600;
    letter-spacing: 0.04em;
    color: var(--pm-bad);
    text-transform: none;
  }
  .hub__nav {
    display: grid;
    gap: 0.85rem;
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 520px) {
    .hub__nav {
      grid-template-columns: 1fr;
    }
  }
  .hub__go {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    padding: 1.35rem 1.25rem;
    border-radius: var(--pm-radius);
    border: 1px solid var(--pm-line);
    background: var(--pm-elevated-bg);
    text-decoration: none;
    color: inherit;
    transition: border-color 0.18s var(--pm-ease-out), transform 0.18s var(--pm-ease-out);
  }
  .hub__go:hover {
    border-color: var(--pm-border-strong);
    transform: translateY(-1px);
  }
  .hub__go-name {
    font-size: 1.35rem;
    font-weight: 700;
    letter-spacing: -0.03em;
    color: var(--pm-ink);
  }
  .hub__go-desc {
    font-size: 0.88rem;
    color: var(--pm-ink-mute);
  }
  .hub__go--live .hub__go-name {
    color: var(--pm-live);
  }
  .hub__go--ai .hub__go-name {
    color: var(--pm-ok, #3d9a6a);
  }
  .hub__pairs-title {
    margin: 0 0 0.55rem;
    font-size: 0.72rem;
    font-weight: 650;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--pm-ink-mute);
  }
  .hub__pairs-grid {
    display: grid;
    gap: 0.45rem 0.65rem;
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  @media (max-width: 640px) {
    .hub__pairs-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
  .hub__pair {
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-rows: auto auto;
    gap: 0.1rem 0.35rem;
    padding: 0.45rem 0.55rem;
    border-radius: 8px;
    border: 1px solid var(--pm-line);
    background: var(--pm-elevated-bg);
    transition:
      border-color 0.18s var(--pm-ease-out, ease),
      box-shadow 0.18s var(--pm-ease-out, ease);
  }
  .hub__pair.is-up {
    border-color: color-mix(in srgb, var(--pm-ok) 48%, var(--pm-line));
    box-shadow:
      0 0 0 1px color-mix(in srgb, var(--pm-ok) 28%, transparent),
      0 0 14px -4px color-mix(in srgb, var(--pm-ok) 42%, transparent);
  }
  .hub__pair.is-down {
    border-color: color-mix(in srgb, var(--pm-bad) 48%, var(--pm-line));
    box-shadow:
      0 0 0 1px color-mix(in srgb, var(--pm-bad) 28%, transparent),
      0 0 14px -4px color-mix(in srgb, var(--pm-bad) 42%, transparent);
  }
  .hub__pair-lab {
    grid-column: 1 / -1;
    font-size: 0.62rem;
    font-weight: 650;
    letter-spacing: 0.06em;
    color: var(--pm-ink-mute);
  }
  .hub__pair-bal {
    font-size: 0.95rem;
    font-weight: 700;
    color: var(--pm-ink);
  }
  .hub__pair-wr {
    font-size: 0.72rem;
    color: var(--pm-ink-mute);
    align-self: end;
  }
</style>
